// src/hooks/use-data.ts
import { api } from "@/lib/api";
import type {
  DeleteGroupResponse,
  EntityDetail,
  EntityListResponse,
  EntityRecord,
  GroupDetail,
  GroupRecord,
  HygieneReport,
  MembershipHealBacklogResponse,
  OpsJobStatus,
  OrphanGroupsResponse,
  PaginatedResponse,
  TargetEnv,
  UpdateEntityResponse,
  UpdateGroupResponse,
} from "@/lib/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * IR-661 Rotem reopen (2026-05-11): append `?target_env=<v>` to a URL
 * so the backend scopes the mutation. Centralised to keep the wire
 * contract consistent across delete/update/bulk handlers.
 */
function withTargetEnv(url: string, env: TargetEnv): string {
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}target_env=${env}`;
}

// ─── Groups ───

export interface GroupFilters {
  q?: string;
  platform?: string;
  classification?: string;
  status?: string;
  risk_min?: number;
  risk_max?: number;
  sort?: string;
  page?: number;
  page_size?: number;
}

export function groupFilterParams(f: GroupFilters): string {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.platform) p.set("platform", f.platform);
  if (f.classification) p.set("classification", f.classification);
  if (f.status) p.set("status", f.status);
  if (f.risk_min != null) p.set("risk_min", String(f.risk_min));
  if (f.risk_max != null) p.set("risk_max", String(f.risk_max));
  if (f.sort) p.set("sort", f.sort);
  if (f.page) p.set("page", String(f.page));
  if (f.page_size) p.set("page_size", String(f.page_size));
  const s = p.toString();
  return s ? `?${s}` : "";
}

export function useGroups(filters: GroupFilters = {}) {
  return useQuery({
    queryKey: ["data", "groups", filters],
    queryFn: () =>
      api<PaginatedResponse<GroupRecord>>(`/api/ops/v1/data/groups${groupFilterParams(filters)}`),
  });
}

export function useGroup(id: string | null) {
  return useQuery({
    queryKey: ["data", "groups", id],
    queryFn: () => api<GroupDetail>(`/api/ops/v1/data/groups/${id}`),
    enabled: !!id,
  });
}

export interface UpdateGroupParams {
  id: string;
  data: Partial<GroupRecord>;
  /** Echoed back as `X-If-Unmodified-Since` for optimistic-concurrency
   *  protection (Phase 6 Task 6). When present and older than the
   *  server's `system_update_time`, backend returns 409 with a
   *  `ConflictBody`. */
  ifUnmodifiedSince?: string | undefined;
  /** IR-661 Rotem reopen (2026-05-11): operator-selected env scope. */
  targetEnv: TargetEnv;
}

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation<UpdateGroupResponse, Error, UpdateGroupParams>({
    mutationFn: (params) => {
      const headers: Record<string, string> = {};
      if (params.ifUnmodifiedSince) {
        headers["X-If-Unmodified-Since"] = params.ifUnmodifiedSince;
      }
      return api<UpdateGroupResponse>(
        withTargetEnv(`/api/ops/v1/data/groups/${params.id}`, params.targetEnv),
        {
          method: "PUT",
          body: JSON.stringify(params.data),
          headers,
        },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["data", "groups"] }),
  });
}

export interface DeleteGroupParams {
  id: string;
  /** IR-661 Rotem reopen (2026-05-11): operator-selected env scope. */
  targetEnv: TargetEnv;
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation<DeleteGroupResponse, Error, DeleteGroupParams>({
    mutationFn: (params) =>
      api<DeleteGroupResponse>(
        withTargetEnv(`/api/ops/v1/data/groups/${params.id}`, params.targetEnv),
        { method: "DELETE" },
      ),
    onSuccess: () => {
      // Phase 5 surface (groups-unified-tab) reads via useUnifiedGroups
      // (use-fleet.ts) under a different query key prefix. Invalidate
      // both so the soft-deleted row drops out of the table without a
      // manual refresh.
      qc.invalidateQueries({ queryKey: ["data", "groups"] });
      qc.invalidateQueries({ queryKey: ["data", "groups-unified"] });
    },
  });
}

export function useBulkGroups() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      ids: string[];
      action: "update_status" | "update_classification";
      payload: Record<string, unknown>;
      /** IR-661 Rotem reopen (2026-05-11): operator-selected env scope. */
      targetEnv: TargetEnv;
    }) => {
      const { targetEnv, ...body } = data;
      return api(withTargetEnv("/api/ops/v1/data/groups/bulk", targetEnv), {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["data", "groups"] }),
  });
}

export function useMergeGroups() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { primary_id: string; merge_ids: string[] }) =>
      api("/api/ops/v1/data/groups/merge", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["data", "groups"] }),
  });
}

const TERMINAL_JOB_STATUSES: ReadonlySet<OpsJobStatus["status"]> = new Set([
  "completed",
  "failed",
  "cancelled",
]);

/**
 * Poll the OpsJob row for a group-delete items-sweep. Stops once the job
 * reaches a terminal status. Disabled when either id is null.
 */
export function useDeleteGroupJobStatus(groupId: string | null, jobId: string | null) {
  return useQuery<OpsJobStatus>({
    queryKey: ["data", "groups", groupId, "delete-job", jobId],
    queryFn: () => api<OpsJobStatus>(`/api/ops/v1/data/groups/${groupId}/delete-job/${jobId}`),
    enabled: !!groupId && !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 5000;
      return TERMINAL_JOB_STATUSES.has(data.status) ? false : 5000;
    },
  });
}

// ─── Entities ───

export interface EntityFilters {
  q?: string;
  platform?: string;
  classification?: string;
  status?: string;
  /** IR-1415: CSV of alpha2 codes; `__none__` selects entities with no country. */
  country?: string;
  risk_min?: number;
  risk_max?: number;
  has_group?: boolean;
  /** IR-1415: inclusive yyyy-MM-dd bounds on when the entity was added. */
  date_from?: string;
  date_to?: string;
  sort?: string;
  page?: number;
  page_size?: number;
}

export function entityFilterParams(f: EntityFilters): string {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.platform) p.set("platform", f.platform);
  if (f.classification) p.set("classification", f.classification);
  if (f.status) p.set("status", f.status);
  if (f.country) p.set("country", f.country);
  if (f.risk_min != null) p.set("risk_min", String(f.risk_min));
  if (f.risk_max != null) p.set("risk_max", String(f.risk_max));
  if (f.has_group != null) p.set("has_group", String(f.has_group));
  if (f.date_from) p.set("date_from", f.date_from);
  if (f.date_to) p.set("date_to", f.date_to);
  if (f.sort) p.set("sort", f.sort);
  if (f.page) p.set("page", String(f.page));
  if (f.page_size) p.set("page_size", String(f.page_size));
  const s = p.toString();
  return s ? `?${s}` : "";
}

export function useEntities(filters: EntityFilters = {}) {
  return useQuery({
    queryKey: ["data", "entities", filters],
    queryFn: () =>
      api<EntityListResponse>(`/api/ops/v1/data/entities${entityFilterParams(filters)}`),
    // IR-1412 r2: background revalidation so the tab stays fresh without a
    // manual refresh; isLoading-gated UIs never flash on these refetches.
    refetchInterval: 120_000,
  });
}

export function useEntity(id: string | null) {
  return useQuery({
    queryKey: ["data", "entities", id],
    queryFn: () => api<EntityDetail>(`/api/ops/v1/data/entities/${id}`),
    enabled: !!id,
  });
}

export interface UpdateEntityParams {
  id: string;
  data: Partial<EntityRecord>;
  /** See `UpdateGroupParams.ifUnmodifiedSince`. */
  ifUnmodifiedSince?: string | undefined;
  /** IR-661 Rotem reopen (2026-05-11): operator-selected env scope. */
  targetEnv: TargetEnv;
}

export function useUpdateEntity() {
  const qc = useQueryClient();
  return useMutation<UpdateEntityResponse, Error, UpdateEntityParams>({
    mutationFn: (params) => {
      const headers: Record<string, string> = {};
      if (params.ifUnmodifiedSince) {
        headers["X-If-Unmodified-Since"] = params.ifUnmodifiedSince;
      }
      return api<UpdateEntityResponse>(
        withTargetEnv(`/api/ops/v1/data/entities/${params.id}`, params.targetEnv),
        {
          method: "PUT",
          body: JSON.stringify(params.data),
          headers,
        },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["data", "entities"] }),
  });
}

export interface DeleteEntityParams {
  id: string;
  /** IR-661 Rotem reopen (2026-05-11): operator-selected env scope. */
  targetEnv: TargetEnv;
}

export function useDeleteEntity() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, DeleteEntityParams>({
    mutationFn: (params) =>
      api(withTargetEnv(`/api/ops/v1/data/entities/${params.id}`, params.targetEnv), {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["data", "entities"] }),
  });
}

export function useBulkEntities() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      ids: string[];
      action: "update_status" | "update_classification";
      payload: Record<string, unknown>;
      /** IR-661 Rotem reopen (2026-05-11): operator-selected env scope. */
      targetEnv: TargetEnv;
    }) => {
      const { targetEnv, ...body } = data;
      return api(withTargetEnv("/api/ops/v1/data/entities/bulk", targetEnv), {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["data", "entities"] }),
  });
}

export function useMergeEntities() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { primary_id: string; merge_ids: string[] }) =>
      api("/api/ops/v1/data/entities/merge", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["data", "entities"] }),
  });
}

export function usePropagateToMembers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      entity_ids: string[];
      strategy: "highest_risk" | "specific_group";
      group_id?: string;
    }) =>
      api("/api/ops/v1/data/entities/propagate", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["data", "entities"] }),
  });
}

// ─── Hygiene ───

export function useHygieneReport() {
  return useQuery({
    queryKey: ["data", "hygiene"],
    queryFn: () => api<HygieneReport>("/api/ops/v1/data/hygiene"),
    enabled: false, // manual trigger only
  });
}

export function useFixHygiene() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      type: string;
      action: string;
      items: Array<Record<string, unknown>>;
    }) =>
      api("/api/ops/v1/data/hygiene/fix", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["data", "hygiene"] }),
  });
}

// ─── Orphan Groups (IR-941 Slice B) ───

export interface OrphanFilters {
  platform?: string;
  data_state?: string;
  age_bucket?: string;
  page?: number;
  page_size?: number;
}

export function orphanFilterParams(f: OrphanFilters): string {
  const p = new URLSearchParams();
  if (f.platform) p.set("platform", f.platform);
  if (f.data_state) p.set("data_state", f.data_state);
  if (f.age_bucket) p.set("age_bucket", f.age_bucket);
  if (f.page) p.set("page", String(f.page));
  if (f.page_size) p.set("page_size", String(f.page_size));
  const s = p.toString();
  return s ? `?${s}` : "";
}

export function useOrphanGroups(filters: OrphanFilters = {}) {
  return useQuery({
    queryKey: ["data", "orphan-groups", filters],
    queryFn: () =>
      api<OrphanGroupsResponse>(`/api/ops/v1/data/orphan-groups${orphanFilterParams(filters)}`),
    // IR-1412 r2: background revalidation (see useEntities).
    refetchInterval: 120_000,
  });
}

// ─── Membership Heal Backlog (IR-903) ───

export function useMembershipHealBacklog(days = 30, platform = "whatsapp") {
  return useQuery({
    queryKey: ["data", "membership-heal-backlog", { days, platform }],
    queryFn: () =>
      api<MembershipHealBacklogResponse>(
        `/api/ops/v1/data/membership-heal-backlog?days=${days}&platform=${encodeURIComponent(platform)}`,
      ),
    refetchInterval: 5 * 60 * 1000, // 5 min
  });
}
